import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSearch, faXmark } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, FeatureType, Grid, ModalSize, User } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { UserComponent } from './user.component'

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        ActionToolbarButtonsComponent,
        FaIconComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class UsersComponent extends BaseComponent {
    faSearch = faSearch
    faXmark = faXmark
    grid: Grid<User>
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }

    constructor() {
        super()

        this.grid = new Grid<User>(this.services)
            .setupModalEditing(this.services.data.users, 'User', UserComponent, () => new User(), this, ModalSize.extraLarge)
            .wrapColumnHeaders()
            .filterBy(item => [item.displayName, item.emailAddress, item.name])
        this.grid.canEdit = this.canEdit
        this.preRefresh()
    }

    preRefresh() {
        this.refresh()
        this.services.data.get<{ inserted: number; updated: number }>('api/User/UpdateUsersFromVista').subscribe({
            next: result => {
                if ((result?.inserted ?? 0) > 0 || (result?.updated ?? 0) > 0) {
                    this.refresh()
                }
            },
        })
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.users.queryAll(),
                    this.services.data.rights.queryAll(),
                    this.services.data.roles.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                    },
                }),
        )
    }

    resetWebAppsUser() {
        this.services.modals
            .confirm('Confirm Reset', 'Are you sure you want to reset the selected WebApps user? They will need to re-register.')
            .then(
                () => {
                    const params = new HttpParams().append('userName', this.grid.selectedItem.name)
                    this.services.data.get('api/User/ResetWebAppsUser', params).subscribe({
                        next: () => {
                            this.services.notification.success('The user was successfully reset')
                        },
                        error: () => {
                            this.services.notification.warning('The user was not successfully reset, and may have already been reset.')
                        },
                    })
                },
                () => {
                    console.log('no')
                },
            )
    }

    setRequireMfa(require: boolean) {
        const user = this.grid.selectedItem as User
        if (!user) return
        const action = require ? 'require authenticator MFA on next sign-in' : 'stop requiring authenticator MFA'
        this.services.modals.confirm('Confirm MFA policy', `Are you sure you want to ${action} for ${user.displayName}?`).then(
            () => {
                this.services.data.post('api/User/SetRequireAuthenticatorMfa', { userIds: [user.id], require }).subscribe({
                    next: () => {
                        user.requireAuthenticatorMfa = require
                        this.services.notification.success(require ? 'MFA will be required on next sign-in.' : 'MFA is no longer required.')
                    },
                    error: () => this.services.notification.error('Could not update MFA requirement.'),
                })
            },
            () => undefined,
        )
    }

    requireMfaForHighRisk() {
        this.services.modals
            .confirm(
                'Require MFA for high-risk users',
                'This turns on authenticator MFA for employees with admin, users/roles, purchasing, or financial rights. Lessors are skipped. They will enroll on next sign-in.',
            )
            .then(
                () => {
                    this.services.data.post<{ updated?: number }>('api/User/RequireMfaForHighRisk', {}).subscribe({
                        next: result => {
                            this.services.notification.success(`MFA required for ${result?.updated ?? 0} high-risk user(s).`)
                            this.refresh()
                        },
                        error: () => this.services.notification.error('Could not update high-risk MFA requirements.'),
                    })
                },
                () => undefined,
            )
    }

    resetMfa() {
        const user = this.grid.selectedItem as User
        if (!user) return
        this.services.modals
            .confirm(
                'Reset MFA',
                `Reset authenticator MFA for ${user.displayName}? Their password is unchanged. If MFA is still required, they will enroll again on next sign-in.`,
            )
            .then(
                () => {
                    this.services.data.post('api/User/ResetMfa', { userName: user.name }).subscribe({
                        next: () => {
                            user.twoFactorEnabled = false
                            this.services.notification.success('MFA was reset. The user must enroll again if MFA is required.')
                        },
                        error: () => this.services.notification.error('Could not reset MFA. The user may not have a WebApps login yet.'),
                    })
                },
                () => undefined,
            )
    }

    deleteUser() {
        this.services.modals
            .confirm(
                'Confirm Permanent Deletion',
                'Are you sure you want to delete user ' +
                    this.grid.selectedItem.displayName +
                    '? You will be prompted a second time to confirm.',
            )
            .then(
                () => {
                    this.grid.deleteItem(this.grid.selectedItem)
                },
                () => {
                    console.log('no')
                },
            )
    }
}
